import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  SafeAreaView,
  RefreshControl,
  TouchableOpacity,
} from "react-native";
import { useRouter } from "expo-router";
import { getEvents, getProfile } from "../../lib/api";
import { EventCard } from "../../../components/EventCard";
import { Ionicons } from "@expo/vector-icons";
import type { Event } from "../../lib/types";

const STATE_NAME_TO_ABBREV: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC",
};
const VALID_ABBREVS = new Set(Object.values(STATE_NAME_TO_ABBREV));

function parseStateInput(input: string): string[] {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      const upper = s.toUpperCase();
      if (VALID_ABBREVS.has(upper)) return upper;
      return STATE_NAME_TO_ABBREV[s.toLowerCase()] || null;
    })
    .filter((s): s is string => s !== null);
}

export default function EventsScreen() {
  const router = useRouter();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [searchMode, setSearchMode] = useState<"state" | "zip">("state");
  const [stateFilter, setStateFilter] = useState("");
  const [zipFilter, setZipFilter] = useState("");
  const [zipRadius, setZipRadius] = useState<50 | 100 | 150 | 200>(100);
  const [userLoaded, setUserLoaded] = useState(false);
  // Profile zip (separate from the search-input zipFilter) — used to attach
  // distance to events even when the user is searching by state.
  const [profileZip, setProfileZip] = useState<string | null>(null);

  async function fetchEvents(stateInput?: string, zip?: string, radius: number = zipRadius, homeZip?: string | null) {
    const effectiveHomeZip = homeZip ?? profileZip ?? undefined;
    try {
      if (zip?.trim()) {
        const data = await getEvents({ zip: zip.trim(), radius });
        setEvents(data);
      } else if (stateInput?.trim()) {
        // Support multiple states by abbrev or full name: "NJ, Pennsylvania, NY"
        const states = parseStateInput(stateInput);
        if (states.length === 0) { setEvents([]); setError(""); setLoading(false); return; }
        const allEvents: Event[] = [];
        for (const st of states) {
          const data = await getEvents({ state: st, homeZip: effectiveHomeZip });
          allEvents.push(...data);
        }
        setEvents(allEvents);
      } else {
        const data = await getEvents({ homeZip: effectiveHomeZip });
        setEvents(data);
      }
      setError("");
    } catch (err: any) {
      setError(err.message || "Failed to load events");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    // Load user's location and auto-search
    getProfile()
      .then((profile) => {
        if (profile.zipCode) setProfileZip(profile.zipCode);
        if (profile.zipCode) {
          setSearchMode("zip");
          setZipFilter(profile.zipCode);
          fetchEvents(undefined, profile.zipCode);
        } else if (profile.state) {
          setSearchMode("state");
          setStateFilter(profile.state);
          // pass home zip explicitly since state setter may not be applied yet
          fetchEvents(profile.state, undefined, undefined, profile.zipCode ?? null);
        } else {
          fetchEvents();
        }
        setUserLoaded(true);
      })
      .catch(() => {
        fetchEvents();
        setUserLoaded(true);
      });
  }, []);

  function onRefresh() {
    setRefreshing(true);
    fetchEvents(stateFilter, zipFilter);
  }

  function handleSearch() {
    setLoading(true);
    fetchEvents(stateFilter, searchMode === "zip" ? zipFilter : undefined);
  }

  // Deduplicate by club + date
  const seen = new Set<string>();
  const deduped = events.filter((e) => {
    const key = `${e.club || e.name}-${e.date}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const sorted = deduped.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const parsedStates = searchMode === "state" ? parseStateInput(stateFilter) : [];

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Filter bar */}
      <View
        style={{
          backgroundColor: "#ffffff",
          paddingHorizontal: 16,
          paddingVertical: 12,
          borderBottomWidth: 1,
          borderBottomColor: "#e5e7eb",
        }}
      >
        {/* Toggle: State / Zip */}
        <View style={{ flexDirection: "row", gap: 8, marginBottom: 10 }}>
          <TouchableOpacity
            onPress={() => setSearchMode("state")}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: searchMode === "state" ? "#1B2A4A" : "#f3f4f6",
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 13, fontWeight: "600", color: searchMode === "state" ? "#fff" : "#6b7280" }}>By State</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setSearchMode("zip")}
            style={{
              flex: 1,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: searchMode === "zip" ? "#1B2A4A" : "#f3f4f6",
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 13, fontWeight: "600", color: searchMode === "zip" ? "#fff" : "#6b7280" }}>By Zip Code</Text>
          </TouchableOpacity>
        </View>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#f3f4f6",
              borderRadius: 10,
              paddingHorizontal: 12,
              paddingVertical: 10,
            }}
          >
            <Ionicons name={searchMode === "zip" ? "navigate-outline" : "location-outline"} size={16} color="#6b7280" />
            {searchMode === "state" ? (
              <TextInput
                value={stateFilter}
                onChangeText={(t) => setStateFilter(t.replace(/[^a-zA-Z, ]/g, "").slice(0, 60))}
                placeholder="State(s) — e.g. TX or NJ, Pennsylvania"
                maxLength={60}
                style={{ flex: 1, marginLeft: 8, fontSize: 16, color: "#111827" }}
                placeholderTextColor="#9ca3af"
                returnKeyType="search"
                onSubmitEditing={handleSearch}
              />
            ) : (
              <TextInput
                value={zipFilter}
                onChangeText={(t) => setZipFilter(t.replace(/[^0-9]/g, "").slice(0, 5))}
                placeholder="Zip code (e.g. 07480)"
                maxLength={5}
                keyboardType="number-pad"
                style={{ flex: 1, marginLeft: 8, fontSize: 16, color: "#111827" }}
                placeholderTextColor="#9ca3af"
                returnKeyType="search"
                onSubmitEditing={handleSearch}
              />
            )}
          </View>
          <TouchableOpacity
            onPress={handleSearch}
            style={{
              backgroundColor: "#1B2A4A",
              paddingHorizontal: 16,
              paddingVertical: 10,
              borderRadius: 10,
            }}
          >
            <Text style={{ color: "#ffffff", fontWeight: "600", fontSize: 14 }}>Search</Text>
          </TouchableOpacity>
        </View>

        {/* Multi-state hint — only show when searching by state */}
        {searchMode === "state" && (
          <Text style={{ fontSize: 11, color: "#6b7280", marginTop: 8, fontStyle: "italic" }}>
            Tip: separate multiple states with a comma (e.g. <Text style={{ fontWeight: "600", fontStyle: "normal" }}>NJ, PA, NY</Text>)
          </Text>
        )}

        {/* Radius pills — only show when searching by zip */}
        {searchMode === "zip" && (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 10 }}>
            <Text style={{ fontSize: 12, color: "#6b7280", fontWeight: "600" }}>Within</Text>
            {([50, 100, 150, 200] as const).map((r) => {
              const selected = zipRadius === r;
              return (
                <TouchableOpacity
                  key={r}
                  onPress={() => {
                    setZipRadius(r);
                    if (zipFilter.trim()) {
                      setLoading(true);
                      fetchEvents(undefined, zipFilter, r);
                    }
                  }}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 16,
                    backgroundColor: selected ? "#1B2A4A" : "#f3f4f6",
                  }}
                >
                  <Text style={{ fontSize: 12, fontWeight: "600", color: selected ? "#fff" : "#6b7280" }}>
                    {r} mi
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>

      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 60 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {loading && !refreshing ? (
          <View style={{ alignItems: "center", paddingVertical: 60 }}>
            <Text style={{ color: "#6b7280" }}>Loading events...</Text>
          </View>
        ) : sorted.length === 0 ? (
          <View
            style={{
              alignItems: "center",
              paddingVertical: 60,
              backgroundColor: "#ffffff",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: "#e5e7eb",
            }}
          >
            <Ionicons name="calendar-outline" size={52} color="#d1d5db" />
            <Text style={{ fontSize: 16, fontWeight: "600", color: "#374151", marginTop: 16 }}>
              No events found
            </Text>
            <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 6, textAlign: "center", paddingHorizontal: 32 }}>
              {searchMode === "zip" && zipFilter
                ? `No upcoming events within ${zipRadius} miles of ${zipFilter}`
                : searchMode === "state" && stateFilter
                ? `No upcoming events in ${stateFilter}`
                : "No upcoming events available"}
            </Text>
          </View>
        ) : (
          <>
            <Text style={{ fontSize: 15, color: "#6b7280", marginBottom: 12 }}>
              {sorted.length} upcoming event{sorted.length !== 1 ? "s" : ""}{searchMode === "zip" && zipFilter ? ` within ${zipRadius} mi` : searchMode === "state" && parsedStates.length > 1 ? ` — across ${parsedStates.length} states` : searchMode === "state" && parsedStates.length === 1 ? ` — entire state` : ""}
            </Text>
            {sorted.map((event) => (
              <EventCard key={event.id} event={event} onPress={(e) => {
                router.push(`/(tabs)/events/${e.id}` as any);
              }} />
            ))}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
